import { useState } from "react";
import { Mail, Instagram, Send } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Contact error:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="contact-section" className="py-24 md:py-32 bg-[#1A3626]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#8C9D88] mb-3 font-medium">
              Get In Touch
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#F9F6F0] tracking-tight mb-6">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-sm text-[#8C9D88] leading-relaxed mb-10">
              Contact us for dance class bookings, sound healing inquiries, or any questions about our offerings.
            </p>

            <div className="space-y-6">
              <a
                data-testid="contact-email-link"
                href="mailto:coral.dozaguass@gmail.com"
                className="flex items-center gap-4 text-[#F9F6F0] hover:text-[#CC5543] transition-colors group"
              >
                <div className="w-10 h-10 rounded-full border border-[#596B5B] flex items-center justify-center group-hover:border-[#CC5543] transition-colors">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#8C9D88] mb-0.5">Email</p>
                  <p className="text-sm">coral.dozaguass@gmail.com</p>
                </div>
              </a>

              <a
                data-testid="contact-instagram-link"
                href="https://www.instagram.com/doz.aguas/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-[#F9F6F0] hover:text-[#CC5543] transition-colors group"
              >
                <div className="w-10 h-10 rounded-full border border-[#596B5B] flex items-center justify-center group-hover:border-[#CC5543] transition-colors">
                  <Instagram size={16} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#8C9D88] mb-0.5">Instagram</p>
                  <p className="text-sm">@doz.aguas</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            {sent ? (
              <div data-testid="contact-success-msg" className="bg-[#243F30] border border-[#596B5B] rounded-lg p-10 text-center">
                <div className="w-12 h-12 rounded-full bg-[#CC5543]/20 flex items-center justify-center mx-auto mb-4">
                  <Send size={20} className="text-[#CC5543]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#F9F6F0] mb-2">
                  Message Sent
                </h3>
                <p className="text-sm text-[#8C9D88]">
                  Thank you! We'll get back to you soon.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 text-xs uppercase tracking-wider text-[#CC5543] hover:text-[#F9F6F0] transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#F9F6F0] mb-2">
                  Send a Message
                </h3>
                <div>
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-transparent border-b border-[#596B5B] text-[#F9F6F0] placeholder-[#596B5B] pb-3 text-sm focus:outline-none focus:border-[#CC5543] transition-colors"
                    required
                  />
                </div>
                <div>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    placeholder="Your Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-transparent border-b border-[#596B5B] text-[#F9F6F0] placeholder-[#596B5B] pb-3 text-sm focus:outline-none focus:border-[#CC5543] transition-colors"
                    required
                  />
                </div>
                <div>
                  <textarea
                    data-testid="contact-message-input"
                    placeholder="Your Message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={4}
                    className="w-full bg-transparent border-b border-[#596B5B] text-[#F9F6F0] placeholder-[#596B5B] pb-3 text-sm focus:outline-none focus:border-[#CC5543] transition-colors resize-none"
                    required
                  />
                </div>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="px-8 py-3.5 bg-[#CC5543] text-white text-xs uppercase tracking-[0.2em] font-medium rounded-full hover:bg-[#A94334] transition-all duration-300 disabled:opacity-60"
                >
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
